/**
 * Prázdný stav — VŽDY s vysvětlením (spec 08 §7).
 *
 * Prázdná obrazovka je chyba návrhu: uživatel nepozná, jestli data nejsou,
 * nebo jestli se něco rozbilo. Každý prázdný stav proto říká CO chybí, PROČ
 * a co s tím.
 */
import type { ReactNode } from 'react';

export interface EmptyStateProps {
  title: string;
  /** proč je prázdno — tohle je ta důležitá část */
  description?: ReactNode;
  /** co může uživatel udělat */
  action?: ReactNode;
  icon?: ReactNode;
  compact?: boolean;
}

export function EmptyState({ title, description, action, icon, compact }: EmptyStateProps) {
  return (
    <div className={`empty-state${compact ? ' empty-state--compact' : ''}`} role="status">
      <div className="empty-state__icon" aria-hidden="true">
        {icon ?? <DefaultIcon />}
      </div>
      <h3 className="empty-state__title">{title}</h3>
      {description ? <div className="empty-state__desc">{description}</div> : null}
      {action ? <div className="empty-state__action">{action}</div> : null}
    </div>
  );
}

function DefaultIcon() {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="none" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M3 9h18" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 13h8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity="0.5" />
    </svg>
  );
}

/**
 * Specializace pro nejčastější případ ze specu: placement bez debug logů.
 * Záložky 5–6 detailu pozice se mají „korektně skrýt s vysvětlením".
 */
export function NoDebugDataState({
  placementId,
  reason,
  hint,
}: {
  placementId?: string;
  /** vysvětlení z API (`EmptyStateOut.reason_cs`) — konkrétnější než náš obecný text */
  reason?: string;
  hint?: string | null;
}) {
  if (reason) {
    return (
      <EmptyState
        title="Tahle záložka nemá pro daný placement data"
        description={
          <>
            <p>{reason}</p>
            {hint ? <p>{hint}</p> : null}
          </>
        }
      />
    );
  }

  return (
    <EmptyState
      title="Tenhle placement se neloguje do debug tabulek"
      description={
        <>
          <p>
            Detailní rozpady (device, prostředí, geometrie kliků) se počítají z{' '}
            <code>log_click_debug</code> / <code>log_load_debug</code>. Ty se plní jen pro
            placementy na whitelistu (<code>isClickDebugPlacement()</code> v adtracku)
            {placementId ? <> a <code>{placementId}</code> na něm není</> : null}.
          </p>
          <p>
            Ostatní záložky fungují — počítají se ze <code>stats_channel</code>, kde jsou
            imprese, kliky a tržby, ale ne souřadnice ani hlavičky.
          </p>
        </>
      }
    />
  );
}

/** Experiment bez potvrzeného mappingu — statistiku nelze spočítat. */
export function NoMappingState({ onReview }: { onReview?: () => void }) {
  return (
    <EmptyState
      title="Experiment nemá potvrzený mapping variant"
      description={
        <p>
          Bez mappingu (které groupy, flagy a pozice patří ke které variantě) nejde
          vyhodnocení spočítat — špatný filtr by znamenal špatnou statistiku. Projdi
          návrh mappingu a potvrď ho.
        </p>
      }
      action={
        onReview ? (
          <button type="button" className="btn btn--primary" onClick={onReview}>
            Projít mapping
          </button>
        ) : null
      }
    />
  );
}
